import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@platform/ui';
import { Download } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Visor a pantalla (casi) completa para un email o un PDF: título, descarga opcional y la cruz de
 * cierre de siempre. Escape y el clic fuera también cierran; el foco vuelve al botón que lo abrió.
 */
export function ViewerDialog({
  open,
  onOpenChange,
  title,
  description,
  download,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  download?: { href: string; filename: string };
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[calc(100dvh-2rem)] max-w-5xl flex-col gap-3 p-4 sm:p-5"
        // Radix enfocaría el primer elemento enfocable: el iframe. Con el foco adentro del iframe las
        // teclas no llegan a la página y Escape no cerraría. Se enfoca la cruz.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (e.target instanceof HTMLElement) {
            e.target.querySelector<HTMLElement>('button[aria-label="Cerrar"]')?.focus();
          }
        }}
      >
        <DialogHeader className="flex-row items-center gap-3 pr-10">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-base">{title}</DialogTitle>
            {description ? (
              <DialogDescription className="truncate">{description}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">Vista ampliada</DialogDescription>
            )}
          </div>
          {download ? (
            <Button asChild size="sm" variant="outline" className="shrink-0">
              <a href={download.href} download={download.filename}>
                <Download /> Descargar
              </a>
            </Button>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border bg-white">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
